import React, {useState} from 'react';
import {Dialog, TextArea, Toast} from 'antd-mobile';
import {t} from "../../../i18n";
import PickUpTrackingNumberScanner from "./PickUpTrackingNumberScanner";
import geolocationService from "../services/PickUpGeolocationService";
import {normalizeParcelScan} from "../../../utils/thirdPartyLabelScanUtils";

interface PickUpTrackingNumberScanCardProps {
    // 运单号与「无法扫描」原因是两类数据，互斥：最多一边有值
    trackingNumbers: string[];
    // 弱网/无网下跳过校验入列的运单号，标红点区分
    unverifiedTrackingNumbers: string[];
    unscannableReason: string | null;
    onTrackingNumbersChange: (next: string[]) => void;
    onMarkUnverified: (trackingNumber: string) => void;
    onUnscannableReasonChange: (next: string | null) => void;
    // 司机位置，随扫描记录一并落库：开扫时取一次，报「无法扫描」时也取一次
    onLocated: (position: {latitude: number; longitude: number} | null) => void;
}

// 列表行：运单号紫色，未校验的运单号红色，无法扫描原因橙色。
// 三色不会撞：扫描与「无法扫描」互斥，橙色行和另两种永不同时出现
const EntryRow: React.FC<{value: string; warning?: boolean; unverified?: boolean; onDelete: () => void}> = ({value, warning, unverified, onDelete}) => (
    <div className={`flex items-center gap-2.5 rounded-lg border p-2.5 mb-2 ${warning ? 'bg-orange-50 border-orange-200' : unverified ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${warning ? 'bg-orange-500' : unverified ? 'bg-red-500' : 'bg-purple-600'}`}/>
        <span className="flex-1 text-sm break-all">{value}</span>
        <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="shrink-0 text-gray-400"
            onClick={onDelete}
        >
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
    </div>
);

// 揽收成功页 运单号扫描模块（数据受控，弹层交互态内收）
const PickUpTrackingNumberScanCard: React.FC<PickUpTrackingNumberScanCardProps> = ({
    trackingNumbers,
    unverifiedTrackingNumbers,
    unscannableReason,
    onTrackingNumbersChange,
    onMarkUnverified,
    onUnscannableReasonChange,
    onLocated,
}) => {
    const [scannerVisible, setScannerVisible] = useState<boolean>(false);
    const [unscannableVisible, setUnscannableVisible] = useState<boolean>(false);
    const [reasonInput, setReasonInput] = useState<string>('');

    // 扫描与「无法扫描」互斥：能扫就逐件扫，扫不了就只填一条原因，不允许两者并存
    const hasScanned = trackingNumbers.length > 0;
    const hasUnscannable = unscannableReason !== null;
    const scanDisabled = hasUnscannable;
    // 原因只允许一条，已有则不能再加（要改就先删掉列表里那条）
    const unscannableDisabled = hasScanned || hasUnscannable;

    const handleScanClick = () => {
        if (scanDisabled) {
            Toast.show({content: t('pages.pickUp.pickedUp.trackingNumberScanDisabledTip'), position: 'center'});
            return;
        }
        captureDriverPosition();
        setScannerVisible(true);
    };

    const handleUnscannableClick = () => {
        if (hasScanned) {
            Toast.show({content: t('pages.pickUp.pickedUp.unscannableDisabledByScanTip'), position: 'center'});
            return;
        }
        if (hasUnscannable) {
            Toast.show({content: t('pages.pickUp.pickedUp.unscannableDisabledByReasonTip'), position: 'center'});
            return;
        }
        setUnscannableVisible(true);
    };

    // 扫描与「无法扫描」两条路共用的取位入口，在动作发生的当下取——那一刻司机就在站点，
    // 坐标才有佐证意义；挪到提交时取会记下司机离站后的位置，反成反证。
    // 「无法扫描」尤其需要：有运单号本身已能证明到场，报不出单号时位置是唯一旁证。
    // 不 await：定位可能耗时数秒，不能卡住扫描或原因提交；
    // 取不到（未授权/室内无信号）就留空，绝不阻断作业
    const captureDriverPosition = () => {
        geolocationService.getFreshPosition()
            .then((pos) => onLocated({latitude: pos.latitude, longitude: pos.longitude}))
            .catch((error) => {
                console.warn('Failed to get driver position for tracking scan record:', error);
                onLocated(null);
            });
    };

    // 关闭即清空，避免取消后残留上次输入
    const closeUnscannable = () => {
        setUnscannableVisible(false);
        setReasonInput('');
    };

    return (
        <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
            <h2 className="text-base font-semibold mb-3">{t('pages.pickUp.pickedUp.trackingNumberScanTitle')}</h2>
            <div
                className="flex items-center justify-center gap-2 rounded-xl py-3.5 text-base font-semibold text-white"
                style={{background: scanDisabled ? '#C7BBF7' : 'var(--adm-color-primary)'}}
                onClick={handleScanClick}
            >
                <svg width="22" height="16" viewBox="0 0 22 16" fill="none" className="shrink-0">
                    <path d="M1 1v3M1 1h4M21 1v3M21 1h-4M1 15v-3M1 15h4M21 15v-3M21 15h-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M4 8h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span>{t('pages.pickUp.pickedUp.scanTrackingNumber')}</span>
            </div>
            <div
                className={`text-center text-sm underline py-3 ${unscannableDisabled ? 'text-gray-300' : 'text-gray-500'}`}
                onClick={handleUnscannableClick}
            >
                {t('pages.pickUp.pickedUp.unscannable')}
            </div>
            {!hasScanned && !hasUnscannable ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-4 text-center text-sm text-gray-400">
                    {t('pages.pickUp.pickedUp.trackingNumberEmpty')}
                </div>
            ) : (
                // 连扫可能上百条，限高滚动，避免把页面撑到看不见底部按钮
                <div className="overflow-y-auto" style={{maxHeight: '240px'}}>
                    {trackingNumbers.map((trackingNumber, index) => (
                        <EntryRow
                            key={trackingNumber}
                            // state 存扫码原文（提交给后端），展示用归一化后的运单号
                            value={normalizeParcelScan(trackingNumber)}
                            unverified={unverifiedTrackingNumbers.includes(trackingNumber)}
                            onDelete={() => onTrackingNumbersChange(trackingNumbers.filter((_, i) => i !== index))}
                        />
                    ))}
                    {unscannableReason !== null && (
                        <EntryRow value={unscannableReason} warning onDelete={() => onUnscannableReasonChange(null)}/>
                    )}
                </div>
            )}
            {/* 连续扫描页：扫到的运单号直接进上面的列表 */}
            <PickUpTrackingNumberScanner
                visible={scannerVisible}
                trackingNumbers={trackingNumbers}
                unverifiedTrackingNumbers={unverifiedTrackingNumbers}
                onChange={onTrackingNumbersChange}
                onMarkUnverified={onMarkUnverified}
                onClose={() => setScannerVisible(false)}
            />

            {/* 无法扫描：填写原因后作为一条不可扫描记录加入列表 */}
            <Dialog
                visible={unscannableVisible}
                title={t('pages.pickUp.pickedUp.unscannable')}
                content={
                    <>
                        <div className="text-sm text-gray-500 mb-2">{t('pages.pickUp.pickedUp.unscannableTip')}</div>
                        <TextArea
                            placeholder={t('pages.pickUp.pickedUp.unscannablePlaceholder')}
                            value={reasonInput}
                            onChange={setReasonInput}
                            maxLength={200}
                            showCount
                            rows={3}
                            className="border border-gray-300 rounded-md px-2 py-1"
                        />
                    </>
                }
                closeOnAction
                onAction={(action) => {
                    if (action.key !== 'confirm') {
                        return;
                    }
                    onUnscannableReasonChange(reasonInput.trim());
                    captureDriverPosition();
                }}
                onClose={closeUnscannable}
                actions={[[
                    {key: 'cancel', text: t('pages.pickUp.pickedUp.cancel')},
                    {key: 'confirm', text: t('pages.pickUp.pickedUp.confirm'), bold: true, disabled: reasonInput.trim() === ''},
                ]]}
            />
        </div>
    );
};

export default PickUpTrackingNumberScanCard;
